import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, clamp01, type Ease, cwave, envelope, inOut, inQuad, keys, outBack, outCubic, outExpo, outQuad, phase, settle, smooth, span, wave } from '../curves.ts';
import { apply, timeline, type KeyPose } from '../pose.ts';

/*
 * Nightmare clips. Every nightmare has at least: walk (loop), attack (the blow lands at ~0.13 s, when the
 * simulation deals it), hit, stun (loop), die (ends on the ground; the view then sinks it) and idle (loop).
 */

/**
 * The strike's ease: it accelerates into the blow, but gentler than a cubic so the swing spreads over the
 * 30 Hz samples (a long limb that turns a radian or more in one sample cuts the corner between them).
 */
const snap: Ease = (k) => k ** 1.5;

/* ---------- goblin: a hunched, scuttling thing that claws ---------- */

type GB = 'root' | 'body' | 'head' | 'jaw' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'legL' | 'shinL' | 'legR' | 'shinR';
type GP = KeyPose<GB>;

/** Crouched, knees bowed, claws forward: the stance everything starts from and comes back to. */
const G_STANCE: GP = {
  body: [0.12, 0, 0],
  head: [-0.1, 0, 0],
  armL: [-0.2, 0, -0.15],
  armR: [-0.2, 0, 0.15],
  foreL: [-0.3, 0, 0],
  foreR: [-0.3, 0, 0],
  legL: [-0.2, 0, -0.08],
  legR: [-0.2, 0, 0.08],
  shinL: [0.3, 0, 0],
  shinR: [0.3, 0, 0],
  root: [0, 0, 0, 0, -0.03, 0],
};

const stance = (b: Bones<GB>, w = 1): void => {
  for (const [k, c] of Object.entries(G_STANCE) as [GB, readonly number[]][]) b[k].rot(c[0]! * w, c[1]! * w, c[2]! * w).pos((c[3] ?? 0) * w, (c[4] ?? 0) * w, (c[5] ?? 0) * w);
};

const goblinIdle = (): Clip<GB> =>
  clip<GB>('idle', 2.4, true, (b, t) => {
    const p = phase(t, 2.4) * TAU;
    stance(b);
    const br = Math.sin(p * 2);
    b.body.rot(-0.05 * br, 0.08 * Math.sin(p), 0.03 * Math.sin(p + 1));
    // A twitchy head: quick looks held a moment.
    const look = Math.sin(p) > 0.3 ? 0.35 : Math.sin(p) < -0.5 ? -0.3 : 0;
    b.head.rot(0.06 * br, look * 0.9 + 0.05 * Math.sin(p * 5), 0.08 * Math.sin(p * 2 + 1));
    b.jaw.rot(0.12 * Math.max(0, Math.sin(p * 6)) ** 4);
    b.armL.rot(0.06 * br, 0, -0.05 * br);
    b.armR.rot(0.06 * Math.sin(p * 2 + 0.6), 0, 0.05 * br);
    b.foreL.rot(-0.12 * Math.max(0, Math.sin(p * 3)));
    b.foreR.rot(-0.12 * Math.max(0, Math.sin(p * 3 + 1.5)));
    b.root.pos(0, -0.012 * (1 + br), 0);
  }, (t) => {
    const br = Math.sin(phase(t, 2.4) * TAU * 2);
    return [1 + 0.02 * br, 1 - 0.025 * br, 1 + 0.02 * br];
  });

/** A quick, bow-legged scuttle; claws swing low. */
const goblinWalk = (dur = 0.56): Clip<GB> =>
  clip<GB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    stance(b);
    b.legL.rot(-0.55 * sn, 0, -0.04 * cs);
    b.legR.rot(0.55 * sn, 0, 0.04 * cs);
    b.shinL.rot(0.75 * Math.max(0, cs) ** 1.4);
    b.shinR.rot(0.75 * Math.max(0, -cs) ** 1.4);
    b.root.pos(0, 0.045 * Math.abs(Math.cos(th)) - 0.02, 0).rot(0, 0, 0.06 * sn);
    b.body.rot(0.14 + 0.05 * Math.cos(2 * th), -0.16 * sn, -0.08 * sn);
    b.head.rot(-0.06 - 0.06 * Math.cos(2 * th - 0.6), 0.12 * sn, 0.06 * sn);
    b.armL.rot(0.5 * Math.sin(th - 0.5), 0, -0.1);
    b.armR.rot(-0.5 * Math.sin(th - 0.5), 0, 0.1);
    b.foreL.rot(-0.3 * Math.max(0, -Math.sin(th - 0.9)));
    b.foreR.rot(-0.3 * Math.max(0, Math.sin(th - 0.9)));
    b.jaw.rot(0.08 * Math.max(0, Math.sin(th * 2)));
  }, (t) => {
    const k = Math.cos(phase(t, dur) * TAU * 2);
    return [1 - 0.03 * k, 1 + 0.04 * k, 1 - 0.03 * k];
  });

/** Rears back for an instant, then both claws rake down as it lunges, jaw wide. */
const goblinAttack = (): Clip<GB> =>
  clip<GB>('attack', 0.6, false, (b, t) => {
    timeline(b, t, [
      [0, G_STANCE],
      [0.07, { ...G_STANCE, body: [-0.3, 0.2, 0], head: [-0.35, 0, 0], jaw: [0.5, 0, 0], armL: [-2.2, 0, -0.4], armR: [-2.4, 0, 0.5], foreL: [-0.7, 0, 0], foreR: [-0.6, 0, 0], root: [0, 0, 0, 0, 0.03, -0.05] }, outQuad],
      [0.14, { ...G_STANCE, body: [0.65, -0.15, 0], head: [0.2, 0, 0], jaw: [0.6, 0, 0], armL: [-0.6, 0, 0.3], armR: [-0.4, 0, -0.35], foreL: [-0.1, 0, 0], foreR: [-0.05, 0, 0], legL: [-0.6, 0, -0.08], shinL: [0.5, 0, 0], legR: [0.2, 0, 0.08], root: [0, 0, 0, 0, -0.08, 0.16] }, snap],
      [0.28, { ...G_STANCE, body: [0.55, -0.2, 0], head: [0.1, 0, 0], jaw: [0.15, 0, 0], armL: [-0.3, 0, 0.35], armR: [-0.2, 0, -0.4], legL: [-0.55, 0, -0.08], shinL: [0.45, 0, 0], legR: [0.18, 0, 0.08], root: [0, 0, 0, 0, -0.07, 0.14] }, outBack],
      [0.6, G_STANCE, inOut],
    ], { foreL: 0.012, foreR: 0.02, head: 0.02, jaw: 0.01 });
  });

const goblinHit = (): Clip<GB> =>
  clip<GB>('hit', 0.36, false, (b, t) => {
    stance(b);
    const k = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    b.body.rot(-0.45 * k, 0.2 * k, 0.1 * k);
    b.head.rot(-0.5 * k, -0.25 * k);
    b.jaw.rot(0.4 * Math.abs(k));
    b.armL.rot(0.4 * k, 0, -0.4 * k);
    b.armR.rot(0.4 * k, 0, 0.4 * k);
    b.root.pos(0, 0, -0.1 * k);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.12 * k, 1 - 0.15 * k, 1 + 0.12 * k];
  });

/** Dazed: it reels on its feet, head lolling round, claws hanging. */
const goblinStun = (): Clip<GB> =>
  clip<GB>('stun', 1.2, true, (b, t) => {
    const p = phase(t, 1.2) * TAU;
    stance(b, 0.6);
    b.root.rot(0.06 * Math.sin(p), 0, 0.12 * Math.cos(p));
    b.body.rot(0.2 + 0.08 * Math.sin(p * 2), 0, -0.1 * Math.cos(p));
    b.head.rot(0.25 * Math.sin(p + 0.8), 0.3 * Math.cos(p + 0.8), 0.25 * Math.sin(p + 0.8));
    b.jaw.rot(0.3);
    b.armL.rot(0.15 * Math.sin(p + 1.4), 0, -0.05);
    b.armR.rot(-0.15 * Math.sin(p + 1.4), 0, 0.05);
    b.foreL.rot(0.1);
    b.foreR.rot(0.1);
  });

/** Struck dead: it arches back, crumples to its knees and pitches forward onto the ground. */
const goblinDie = (): Clip<GB> =>
  clip<GB>('die', 0.95, false, (b, t) => {
    timeline(b, t, [
      [0, G_STANCE],
      [0.12, { ...G_STANCE, body: [-0.55, 0.1, 0.1], head: [-0.6, 0.2, 0], jaw: [0.6, 0, 0], armL: [-1.0, 0, -0.9], armR: [-0.9, 0, 0.9], root: [0, 0, 0, 0, 0.02, -0.06] }, outExpo],
      [0.4, { body: [0.3, 0, 0.1], head: [0.4, 0.3, 0], jaw: [0.3, 0, 0], armL: [-0.3, 0, -0.3], armR: [-0.2, 0, 0.4], legL: [-1.4, 0, -0.2], shinL: [2.0, 0, 0], legR: [-1.3, 0, 0.2], shinR: [2.0, 0, 0], root: [0, 0, 0, 0, -0.3, 0] }, inQuad],
      [0.66, { body: [1.05, 0, 0.15], head: [0.3, 0.6, 0.3], jaw: [0.35, 0, 0], armL: [-2.2, 0, -0.5], armR: [-1.9, 0, 0.7], foreL: [0.2, 0, 0], legL: [-0.7, 0, -0.2], shinL: [1.4, 0, 0], legR: [-0.6, 0, 0.2], shinR: [1.3, 0, 0], root: [0.55, 0, 0, 0, -0.42, 0.2] }, inQuad],
      [0.95, { body: [1.0, 0, 0.15], head: [0.35, 0.65, 0.35], jaw: [0.3, 0, 0], armL: [-2.25, 0, -0.55], armR: [-1.95, 0, 0.72], foreL: [0.2, 0, 0], legL: [-0.65, 0, -0.2], shinL: [1.3, 0, 0], legR: [-0.55, 0, 0.2], shinR: [1.25, 0, 0], root: [0.62, 0, 0, 0, -0.44, 0.22] }, outBack],
    ], { head: 0.03, foreL: 0.02, foreR: 0.02 });
  });

export const goblinClips = (): Clip[] => [goblinIdle(), goblinWalk(), goblinAttack(), goblinHit(), goblinStun(), goblinDie()] as Clip[];

/* ---------- shared ---------- */

const sin = Math.sin;
const cos = Math.cos;
const pos0 = (x: number): number => (x > 0 ? x : 0);
/** A decaying tremble (rattle, shudder): `freq` Hz, gone by about 1/`damp` s. */
const tremble = (t: number, freq: number, damp: number): number => (t < 0 ? 0 : Math.exp(-damp * t) * sin(TAU * freq * t));
/** Squash that keeps the volume: `k` > 0 stretches up, < 0 spreads. */
const vol = (k: number): [number, number, number] => {
  const w = 1 / Math.sqrt(1 + k);
  return [w, 1 + k, w];
};

/* ---------- wolf: a lean shadow that lopes and lunges to bite ---------- */

type WB = 'root' | 'body' | 'chest' | 'head' | 'jaw' | 'foreL' | 'foreL2' | 'foreR' | 'foreR2' | 'hindL' | 'hindL2' | 'hindR' | 'hindR2' | 'tail1' | 'tail2' | 'tail3';
type WP = KeyPose<WB>;

/** Head a touch low, tail carried low and straight: a hunting carriage. */
const W_STANCE: WP = {
  head: [0.08, 0, 0],
  tail1: [-0.35, 0, 0],
  tail2: [-0.12, 0, 0],
  tail3: [-0.08, 0, 0],
  foreL2: [0.04, 0, 0],
  foreR2: [0.04, 0, 0],
  hindL: [0.06, 0, 0],
  hindR: [0.06, 0, 0],
  hindL2: [-0.06, 0, 0],
  hindR2: [-0.06, 0, 0],
};

const wolfIdle = (): Clip<WB> =>
  clip<WB>('idle', 3.6, true, (b, t) => {
    const p = phase(t, 3.6);
    apply(b, W_STANCE);
    const br = wave(p, 0, 3);
    // Breath in the ribs, a weight shift, ears forward: it scents the air, looks off, looks back.
    b.chest.rot(-0.015 * br).scale(1 + 0.012 * br);
    b.body.rot(0.01 * br, 0.03 * wave(p), 0.02 * wave(p, 0.2));
    b.root.pos(0, -0.006 * (1 + br), 0);
    const look = smooth(clamp01(wave(p, 0.1) * 1.6)) - smooth(clamp01(-wave(p, 0.1) * 1.6));
    const sniff = pos0(wave(p, 0.35, 2)) ** 6;
    b.head.rot(-0.1 * sniff + 0.025 * br, 0.38 * look, -0.06 * look);
    b.jaw.rot(0.07 + 0.05 * pos0(wave(p, 0, 6)) ** 3);
    b.tail1.rot(0.03 * br, 0.14 * wave(p, 0.15), 0);
    b.tail2.rot(0, 0.18 * wave(p, 0.05), 0);
    b.tail3.rot(0, 0.2 * wave(p, -0.05), 0);
    b.foreL.rot(0, 0, -0.02 * wave(p, 0.2));
    b.foreR.rot(0, 0, -0.02 * wave(p, 0.2));
  });

/**
 * A loping gallop, one stride a loop: the hind pair drives (a beat apart), then the fore pair reaches; the
 * back flexes and stretches, the head counters the pitch and the tail streams in a travelling wave.
 */
const wolfWalk = (dur = 0.5): Clip<WB> =>
  clip<WB>('walk', dur, true, (b, t) => {
    const p = phase(t, dur);
    apply(b, W_STANCE, 0.6);
    const leg = (thigh: WB, low: WB, off: number, A: number, fold: number): void => {
      const s = wave(p, -off), c = cwave(p, -off);
      b[thigh].rot(-A * s + 0.05);
      // The lower leg folds through the swing (moving forward) and straightens to land.
      b[low].rot(fold * pos0(c) ** 1.4 - 0.08 * pos0(-c));
    };
    leg('hindL', 'hindL2', 0, 0.62, 0.7);
    leg('hindR', 'hindR2', 0.08, 0.62, 0.7);
    leg('foreL', 'foreL2', 0.47, 0.72, 1.1);
    leg('foreR', 'foreR2', 0.55, 0.72, 1.1);
    // Gathered under (hind forward, fore back) the spine arches; stretched out it flattens.
    const gather = wave(p, 0.25);
    b.body.rot(0.09 * wave(p, 0.05), 0, 0.03 * wave(p, 0.3));
    b.chest.rot(-0.13 * gather);
    b.root.pos(0, 0.045 * pos0(wave(p, 0.12)) - 0.03 + 0.012 * wave(p, 0.3, 2), 0);
    b.head.rot(0.12 + 0.12 * gather - 0.09 * wave(p, -0.05), 0.04 * wave(p, 0.1), 0);
    b.jaw.rot(0.18 + 0.06 * wave(p, 0.2, 2));
    b.tail1.rot(0.1 + 0.12 * wave(p, -0.15), 0.06 * wave(p, 0.1), 0);
    b.tail2.rot(0.14 * wave(p, -0.3), 0.08 * wave(p, -0.05), 0);
    b.tail3.rot(0.18 * wave(p, -0.45), 0.1 * wave(p, -0.2), 0);
  }, (t) => {
    const k = wave(phase(t, dur), 0.4);
    return [1 - 0.015 * k, 1 + 0.03 * k, 1 - 0.015 * k];
  });

/** Gathers back on its haunches, launches, the jaws gape and snap shut on the target, it shakes and lets go. */
const wolfAttack = (): Clip<WB> =>
  clip<WB>('attack', 0.66, false, (b, t) => {
    const coil: WP = { ...W_STANCE, root: [0, 0, 0, 0, -0.05, -0.07], body: [-0.08, 0, 0], chest: [0.14, 0, 0], head: [-0.32, 0, 0], jaw: [0.3, 0, 0], foreL: [0.1, 0, 0], foreR: [0.12, 0, 0], foreL2: [0.35, 0, 0], foreR2: [0.35, 0, 0], hindL: [-0.35, 0, 0], hindR: [-0.3, 0, 0], hindL2: [0.4, 0, 0], hindR2: [0.4, 0, 0], tail1: [-0.6, 0, 0] };
    const gape: WP = { ...coil, root: [0, 0, 0, 0, -0.01, 0.1], body: [0.06, 0, 0], chest: [0.02, 0, 0], head: [-0.05, 0, 0], jaw: [0.8, 0, 0], foreL: [-0.6, 0, 0], foreR: [-0.5, 0, 0], foreL2: [0.25, 0, 0], foreR2: [0.3, 0, 0], hindL: [0.3, 0, 0], hindR: [0.25, 0, 0], hindL2: [-0.1, 0, 0], hindR2: [-0.1, 0, 0], tail1: [-0.3, 0, 0] };
    const bite: WP = { ...gape, root: [0, 0, 0, 0, -0.03, 0.22], body: [0.14, 0, 0], chest: [0.12, 0, 0], head: [0.3, 0, 0], jaw: [-0.02, 0, 0], foreL: [-0.85, 0, 0], foreR: [-0.75, 0, 0], foreL2: [-0.05, 0, 0], foreR2: [0, 0, 0], hindL: [0.55, 0, 0], hindR: [0.5, 0, 0], hindL2: [-0.25, 0, 0], hindR2: [-0.2, 0, 0], tail1: [-0.05, 0, 0] };
    const hold: WP = { ...bite, root: [0, 0, 0, 0, -0.06, 0.2], body: [0.1, 0, 0], chest: [0.16, 0, 0], head: [0.36, 0, 0], foreL: [-0.45, 0, 0], foreR: [-0.4, 0, 0], foreL2: [0.15, 0, 0], foreR2: [0.15, 0, 0], hindL: [0.3, 0, 0], hindR: [0.28, 0, 0], hindL2: [0, 0, 0], hindR2: [0, 0, 0], tail1: [-0.2, 0, 0] };
    timeline(b, t, [
      [0, W_STANCE],
      [0.07, coil, outQuad],
      [0.105, gape, inQuad],
      [0.135, bite, snap],
      [0.3, hold, outQuad],
      [0.66, W_STANCE, inOut],
    ], { head: 0.008, tail1: 0.03, tail2: 0.05, tail3: 0.07, hindL: 0.01, hindR: 0.02 });
    // Teeth set, it worries the prey: a savage shake of the head that dies away.
    const shake = tremble(t - 0.14, 5, 7) * span(t, 0.13, 0.17);
    b.head.rot(0, 0.32 * shake, 0.18 * shake);
    b.chest.rot(0, -0.1 * shake, 0);
    b.tail2.rot(0, 0.25 * tremble(t - 0.18, 4, 5), 0);
    b.tail3.rot(0, 0.3 * tremble(t - 0.22, 4, 5), 0);
  });

const wolfHit = (): Clip<WB> =>
  clip<WB>('hit', 0.4, false, (b, t) => {
    apply(b, W_STANCE);
    const k = settle(t, 2.4, 7.5) * span(t, 0, 0.03, outQuad);
    const a = Math.abs(k);
    b.root.pos(0, -0.03 * a, -0.09 * k);
    b.body.rot(-0.12 * k, 0.12 * k, 0.06 * k);
    b.chest.rot(-0.22 * k);
    b.head.rot(-0.45 * k, -0.3 * k, 0.1 * k);
    b.jaw.rot(0.55 * a);
    b.foreL.rot(-0.35 * k);
    b.foreR.rot(-0.3 * k);
    b.foreL2.rot(0.2 * a);
    b.foreR2.rot(0.2 * a);
    b.tail1.rot(-0.6 * a);
    b.tail2.rot(-0.3 * settle(t - 0.04, 2.4, 7));
    b.tail3.rot(-0.3 * settle(t - 0.07, 2.4, 7));
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.08 * k, 1 - 0.1 * k, 1 + 0.08 * k];
  });

/** Dazed: legs braced wide, it sways, head low and shaking slowly as if to clear it. */
const wolfStun = (): Clip<WB> =>
  clip<WB>('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6);
    apply(b, W_STANCE, 0.5);
    b.root.pos(0, -0.04, 0).rot(0.03 * wave(p, 0.25), 0, 0.08 * wave(p));
    b.body.rot(0, 0.05 * wave(p, 0.1), -0.04 * wave(p));
    b.chest.rot(0.12, 0, 0.05 * wave(p, 0.1));
    b.head.rot(0.45 + 0.08 * wave(p, 0, 2), 0.35 * wave(p, 0.15), 0.25 * wave(p, 0.2));
    b.jaw.rot(0.3 + 0.08 * wave(p, 0.3, 2));
    b.foreL.rot(-0.1, 0, -0.15);
    b.foreR.rot(-0.1, 0, 0.15);
    b.hindL.rot(0.05, 0, -0.12);
    b.hindR.rot(0.05, 0, 0.12);
    b.foreL2.rot(0.05 * wave(p, 0.3));
    b.tail1.rot(-0.45, 0.1 * wave(p, 0.2), 0);
    b.tail2.rot(-0.2, 0.12 * wave(p, 0.05), 0);
    b.tail3.rot(-0.1, 0.12 * wave(p, -0.1), 0);
  });

/** A yelp as it rears, the legs give, and it rolls onto its side; one last kick and it lies still. */
const wolfDie = (): Clip<WB> =>
  clip<WB>('die', 1.1, false, (b, t) => {
    const down: WP = { root: [0, 0, 1.47, 0.43, 0.15, 0], body: [0, 0, 0], chest: [0.05, 0, 0], head: [0.2, -0.3, 0], jaw: [0.35, 0, 0], foreL: [-0.45, 0, 0.1], foreR: [-0.3, 0, -0.05], foreL2: [0.55, 0, 0], foreR2: [0.4, 0, 0], hindL: [0.35, 0, 0.1], hindR: [0.5, 0, -0.05], hindL2: [0.3, 0, 0], hindR2: [0.2, 0, 0], tail1: [-0.3, -0.2, 0], tail2: [0.1, -0.1, 0], tail3: [0.1, 0, 0] };
    timeline(b, t, [
      [0, W_STANCE],
      [0.12, { ...W_STANCE, root: [0, 0, 0, 0, 0.02, -0.05], body: [-0.12, 0, 0], chest: [-0.3, 0, 0], head: [-0.65, 0.2, 0], jaw: [0.75, 0, 0], foreL: [-0.6, 0, 0], foreR: [-0.5, 0, 0], foreL2: [0.4, 0, 0], foreR2: [0.5, 0, 0], tail1: [-0.7, 0, 0] }, outExpo],
      [0.42, { root: [0, 0, 0.35, 0.04, -0.2, 0], body: [0.1, 0, 0], chest: [0.15, 0, 0], head: [0.35, -0.2, 0.1], jaw: [0.5, 0, 0], foreL: [-0.2, 0, 0], foreR: [-0.3, 0, 0], foreL2: [1.0, 0, 0], foreR2: [1.1, 0, 0], hindL: [-0.6, 0, 0], hindR: [-0.5, 0, 0], hindL2: [0.9, 0, 0], hindR2: [0.8, 0, 0], tail1: [-0.6, 0, 0] }, inQuad],
      [0.72, down, inQuad],
      [0.86, { ...down, hindR: [0.15, 0, -0.05], hindR2: [0.05, 0, 0], foreR: [-0.55, 0, -0.05], root: [0, 0, 1.43, 0.43, 0.15, 0] }, outQuad],
      [1.1, down, inOut],
    ], { head: 0.04, jaw: 0.02, tail1: 0.03, tail2: 0.06, tail3: 0.09 });
  }, (t) => {
    const k = Math.max(0, settle(t - 0.7, 3, 10)) * span(t, 0.69, 0.71);
    return [1 + 0.06 * k, 1 - 0.08 * k, 1 + 0.06 * k];
  });

const wolfClips = (): Clip[] => [wolfIdle(), wolfWalk(), wolfAttack(), wolfHit(), wolfStun(), wolfDie()] as Clip[];

/* ---------- skeleton: a disciplined dead soldier, shield up, blade at the ready ---------- */

type SB = 'root' | 'hips' | 'body' | 'head' | 'jaw' | 'armL' | 'foreL' | 'legL' | 'shinL' | 'armR' | 'foreR' | 'legR' | 'shinR' | 'blade';
type SP = KeyPose<SB>;

/**
 * The `blade` X channel is absolute, as for the heroes: the blade's angle from straight up toward the front
 * (π/2 points it ahead, π down), whatever the arm does. `sclip` turns it into the wrist's own rotation.
 */
const aimBlade = (b: Bones<SB>, w = 1): void => {
  b.blade.rx -= (b.armR.rx + b.foreR.rx + b.body.rx + b.hips.rx) * w;
};

const sclip = (name: string, dur: number, loop: boolean, pose: (b: Bones<SB>, t: number) => void): Clip<SB> =>
  clip<SB>(name, dur, loop, (b, t) => {
    pose(b, t);
    aimBlade(b);
  });

/** Shield raised before the ribs, blade held up and forward: on guard. */
const S_GUARD: SP = {
  armL: [-0.4, 0.25, -0.1],
  foreL: [-0.75, 0, 0],
  armR: [-0.2, 0, 0.12],
  foreR: [-0.85, 0, 0],
  blade: [0.3, 0, 0],
  legL: [-0.06, 0, -0.02],
  legR: [0.08, 0, 0.02],
  shinL: [0.08, 0, 0],
  shinR: [0.06, 0, 0],
  head: [0.06, 0, 0],
};

const skeletonIdle = (): Clip<SB> =>
  sclip('idle', 3.2, true, (b, t) => {
    const p = phase(t, 3.2);
    apply(b, S_GUARD);
    // No breath, only the slow sway of a dead thing kept upright, and a skull that turns to listen.
    const sw = wave(p);
    b.hips.rot(0, 0.04 * wave(p, 0.2), 0.02 * sw);
    b.body.rot(0.02 * wave(p, 0.1, 2), 0.05 * wave(p, 0.3), -0.025 * sw);
    const look = smooth(clamp01(wave(p, 0.6) * 2)) - 0.6 * smooth(clamp01(-wave(p, 0.6) * 2));
    b.head.rot(0.03 * wave(p, 0, 2), 0.4 * look, 0.08 * look);
    // The jaw chatters now and then.
    b.jaw.rot(0.14 * pos0(wave(p, 0.7)) ** 8 * (0.5 + 0.5 * pos0(wave(p, 0, 16))));
    b.armR.rot(0.03 * wave(p, 0.4, 2));
    b.blade.rot(0.05 * wave(p, 0.3, 2));
    b.armL.rot(0.025 * wave(p, 0.5, 2));
    b.legL.rot(0, 0, -0.02 * sw);
    b.legR.rot(0, 0, -0.02 * sw);
  });

/** A rigid march: stiff knees, a jolt at every footfall, shield held still and the jaw clacking to the step. */
const skeletonWalk = (dur = 0.9): Clip<SB> =>
  sclip('walk', dur, true, (b, t) => {
    const p = phase(t, dur);
    const th = p * TAU;
    const sn = sin(th), cs = cos(th);
    apply(b, S_GUARD, 0.9);
    b.legL.rot(-0.42 * sn);
    b.legR.rot(0.42 * sn);
    b.shinL.rot(0.75 * pos0(cs) ** 2);
    b.shinR.rot(0.75 * pos0(-cs) ** 2);
    const jolt = Math.abs(cs) ** 6;
    b.root.pos(0, 0.025 * Math.abs(sn) - 0.012 - 0.01 * jolt, 0);
    b.hips.rot(0, 0.1 * sn, 0.035 * sn);
    b.body.rot(0.04 + 0.025 * jolt, -0.12 * sn, -0.03 * sn);
    b.head.rot(-0.02 + 0.06 * jolt, 0.05 * sn, 0.03 * sn);
    b.jaw.rot(0.12 * jolt);
    b.armL.rot(0.06 * sin(th - 0.5));
    b.armR.rot(-0.14 * sin(th - 0.5));
    b.foreR.rot(-0.08 * pos0(sin(th - 0.9)));
    b.blade.rot(0.1 * sin(th - 1.2));
  });

/** The blade swings up behind the skull, then one sharp diagonal cut with a step in; it snaps back to guard. */
const skeletonAttack = (): Clip<SB> =>
  sclip('attack', 0.62, false, (b, t) => {
    timeline(b, t, [
      [0, S_GUARD],
      [0.06, { ...S_GUARD, hips: [0, 0.12, 0], body: [-0.12, 0.42, 0.05], head: [-0.05, -0.25, 0], armR: [-2.55, 0, 0.2], foreR: [-0.5, 0, 0], blade: [-1.0, 0, 0], armL: [-0.55, 0.3, -0.15], foreL: [-0.8, 0, 0], legL: [-0.12, 0, -0.02], legR: [0.15, 0, 0.02] }, outQuad],
      [0.135, { ...S_GUARD, root: [0, 0, 0, 0, -0.03, 0.08], hips: [0, -0.1, 0], body: [0.28, -0.5, -0.05], head: [0.12, 0.2, 0], jaw: [0.25, 0, 0], armR: [-0.85, -0.25, -0.35], foreR: [-0.2, 0, 0], blade: [1.95, 0, 0], armL: [-0.35, 0.2, -0.2], foreL: [-0.7, 0, 0], legL: [-0.45, 0, -0.02], shinL: [0.35, 0, 0], legR: [0.28, 0, 0.02], shinR: [0.1, 0, 0] }, snap],
      [0.27, { ...S_GUARD, root: [0, 0, 0, 0, -0.04, 0.08], hips: [0, -0.14, 0], body: [0.34, -0.6, -0.06], head: [0.12, 0.25, 0], jaw: [0.1, 0, 0], armR: [-0.3, -0.35, -0.5], foreR: [-0.1, 0, 0], blade: [2.5, 0, 0], armL: [-0.3, 0.15, -0.2], foreL: [-0.65, 0, 0], legL: [-0.42, 0, -0.02], shinL: [0.35, 0, 0], legR: [0.26, 0, 0.02], shinR: [0.1, 0, 0] }, outBack],
      [0.62, S_GUARD, inOut],
    ], { blade: 0.012, head: 0.02, jaw: 0.01, foreR: 0.008 });
  });

/** The bones rattle loose for an instant: the skull jolts back, the jaw drops, everything shivers. */
const skeletonHit = (): Clip<SB> =>
  sclip('hit', 0.4, false, (b, t) => {
    apply(b, S_GUARD);
    const k = settle(t, 2.3, 8) * span(t, 0, 0.03, outQuad);
    const r = tremble(t, 16, 9);
    b.root.pos(0, 0, -0.07 * k);
    b.body.rot(-0.3 * k, 0.15 * k, 0.04 * r);
    b.head.rot(-0.45 * settle(t - 0.02, 2.3, 8) * span(t, 0, 0.05), -0.2 * k, 0.12 * r);
    b.jaw.rot(0.45 * Math.abs(k));
    b.armL.rot(0.35 * k, 0, -0.1 * k);
    b.armR.rot(0.4 * k, 0, 0.25 * k);
    b.blade.rot(-0.3 * settle(t - 0.03, 2.3, 7));
    b.hips.rot(0, 0, 0.05 * r);
  });

/** Dazed: guard dropped, the skull lolls round on its neck, jaw hanging, it reels in place. */
const skeletonStun = (): Clip<SB> =>
  sclip('stun', 1.4, true, (b, t) => {
    const p = phase(t, 1.4);
    apply(b, S_GUARD, 0.3);
    b.root.rot(0.04 * wave(p, 0.25), 0, 0.06 * wave(p));
    b.body.rot(0.18 + 0.05 * wave(p, 0, 2), 0.06 * wave(p, 0.1), -0.06 * wave(p));
    b.head.rot(0.3 + 0.2 * wave(p, 0.15), 0.35 * cwave(p, 0.15), 0.3 * wave(p, 0.15));
    b.jaw.rot(0.42 + 0.06 * wave(p, 0, 3));
    b.armL.rot(0.1 * wave(p, 0.3), 0, -0.05);
    b.armR.rot(0.15 - 0.1 * wave(p, 0.3), 0, 0.05);
    b.blade.rot(1.9 + 0.15 * wave(p, 0.4));
    b.legL.rot(-0.05, 0, -0.04);
    b.legR.rot(0.05, 0, 0.04);
    b.shinL.rot(0.15);
    b.shinR.rot(0.12);
  });

/** Its binding fails: the knees fold, it drops into a heap of bones and the skull rolls off. */
const skeletonDie = (): Clip<SB> =>
  clip<SB>('die', 1.15, false, (b, t) => {
    const heap: SP = { hips: [0.1, 0.2, 0.08, 0, -0.78, 0.02], body: [-1.35, 0.25, 0.2, 0, -0.04, 0], head: [0.9, 0.8, 1.2, 0.12, 0.08, -0.26], jaw: [0.5, 0, 0], armL: [0.4, 0.4, -0.9], foreL: [-0.4, 0, 0], armR: [0.6, -0.3, 1.0], foreR: [-0.3, 0, 0], blade: [1.2, 0, 0.3], legL: [-1.45, 0, -0.35], shinL: [0.25, 0, 0], legR: [-1.4, 0.2, 0.4], shinR: [0.4, 0, 0] };
    timeline(b, t, [
      [0, S_GUARD],
      [0.1, { ...S_GUARD, body: [-0.3, 0.15, 0.08], head: [-0.5, 0.2, 0], jaw: [0.6, 0, 0], armL: [-0.2, 0.2, -0.5], armR: [-0.4, 0, 0.6], blade: [0.2, 0, 0] }, outExpo],
      [0.45, { hips: [0.15, 0.1, 0.05, 0, -0.42, 0], body: [0.35, 0.1, 0.1], head: [0.35, 0.3, 0.2], jaw: [0.55, 0, 0], armL: [0.3, 0, -0.4], foreL: [-0.3, 0, 0], armR: [0.4, 0, 0.5], foreR: [-0.3, 0, 0], blade: [0.8, 0, 0], legL: [-1.05, 0, -0.1], shinL: [1.7, 0, 0], legR: [-0.95, 0, 0.1], shinR: [1.8, 0, 0] }, inQuad],
      [0.78, heap, inQuad],
      [0.92, { ...heap, hips: [0.1, 0.2, 0.08, 0, -0.74, 0.02], head: [1.3, 1.2, 1.6, 0.16, 0.06, -0.26] }, outQuad],
      [1.15, { ...heap, head: [1.6, 1.5, 1.65, 0.2, 0.08, -0.26] }, inOut],
    ], { head: 0.05, jaw: 0.03, armL: 0.03, armR: 0.02, blade: 0.04 });
    aimBlade(b, 1 - span(t, 0.1, 0.5));
  });

const skeletonClips = (): Clip[] => [skeletonIdle(), skeletonWalk(), skeletonAttack(), skeletonHit(), skeletonStun(), skeletonDie()] as Clip[];

/* ---------- wraith: a hooded robe that drifts, reaches and rakes ---------- */

type RB = 'root' | 'body' | 'head' | 'armL' | 'armR' | 'wispA' | 'wispB';

const wraithIdle = (): Clip<RB> =>
  clip<RB>('idle', 3.2, true, (b, t) => {
    const p = phase(t, 3.2);
    // It hangs in the air, rising and sinking; the robe sways a beat behind, the wisps behind that.
    b.root.pos(0, 0.065 * wave(p), 0).rot(0, 0.08 * wave(p, 0.1), 0);
    b.body.rot(0.05 * wave(p, -0.15) + 0.03 * wave(p, 0.2, 2), 0, 0.04 * wave(p, 0.3));
    b.head.rot(0.06 * wave(p, -0.3) + 0.04, 0.12 * wave(p, 0.4), -0.06 * wave(p, 0.15));
    b.armL.rot(0.12 * wave(p, -0.2), 0.08 * wave(p, 0.1), -0.06 * wave(p, 0.25, 2));
    b.armR.rot(0.12 * wave(p, -0.35), -0.08 * wave(p, 0.2), 0.06 * wave(p, 0.4, 2));
    b.wispA.rot(0.18 * wave(p, -0.3), 0, 0.12 * wave(p, 0.1, 2));
    b.wispB.rot(0.16 * wave(p, -0.45), 0, -0.14 * wave(p, 0.35, 2));
  });

/** Drifting forward: it leans into the glide, claws reaching, robe and wisps streaming back. */
const wraithWalk = (dur = 1.6): Clip<RB> =>
  clip<RB>('walk', dur, true, (b, t) => {
    const p = phase(t, dur);
    b.root.pos(0, 0.05 * wave(p, 0, 2), 0).rot(0, 0, 0.05 * wave(p));
    b.body.rot(0.24 + 0.04 * wave(p, -0.12, 2), 0.05 * wave(p, 0.1), 0.04 * wave(p, -0.1));
    b.head.rot(-0.14 + 0.04 * wave(p, -0.25, 2), -0.05 * wave(p), 0);
    b.armL.rot(-0.15 + 0.14 * wave(p, 0), 0.1, -0.05);
    b.armR.rot(-0.15 + 0.14 * wave(p, 0.5), -0.1, 0.05);
    b.wispA.rot(0.5 + 0.14 * wave(p, -0.2, 2), 0, 0.1 * wave(p, 0.1));
    b.wispB.rot(0.45 + 0.14 * wave(p, -0.35, 2), 0, -0.1 * wave(p, 0.3));
  });

/** Rears up and draws a claw back, then rakes it across in a wide arc as it surges in. */
const wraithAttack = (): Clip<RB> =>
  clip<RB>('attack', 0.7, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.06, { root: [0, 0.15, 0, 0, 0.1, -0.06], body: [-0.25, 0.3, 0.05], head: [-0.2, -0.15, 0], armR: [-1.1, 0.45, 0.55], armL: [-0.35, -0.2, -0.2], wispA: [-0.2, 0, 0], wispB: [-0.25, 0, 0] }, outQuad],
      [0.135, { root: [0, -0.2, 0, 0, -0.02, 0.2], body: [0.38, -0.35, -0.05], head: [0.2, 0.15, 0], armR: [0.45, -0.95, -0.35], armL: [0.2, -0.1, -0.15], wispA: [0.3, 0, 0], wispB: [0.25, 0, 0] }, snap],
      [0.3, { root: [0, -0.25, 0, 0, -0.02, 0.18], body: [0.32, -0.45, -0.06], head: [0.15, 0.2, 0], armR: [0.6, -1.15, -0.4], armL: [0.15, -0.05, -0.1], wispA: [0.6, 0, 0.1], wispB: [0.55, 0, -0.1] }, outBack],
      [0.7, {}, inOut],
    ], { head: 0.02, armL: 0.02, wispA: 0.06, wispB: 0.09 });
  });

const wraithHit = (): Clip<RB> =>
  clip<RB>('hit', 0.42, false, (b, t) => {
    const k = settle(t, 2.2, 7) * span(t, 0, 0.03, outQuad);
    const a = Math.abs(k);
    b.root.pos(0, 0.04 * a, -0.11 * k);
    b.body.rot(-0.35 * k, 0.12 * k, 0.06 * k);
    b.head.rot(-0.4 * settle(t - 0.02, 2.2, 7) * span(t, 0, 0.05), -0.2 * k, 0);
    b.armL.rot(-0.3 * k, 0, -0.45 * k);
    b.armR.rot(-0.3 * k, 0, 0.45 * k);
    b.wispA.rot(-0.4 * settle(t - 0.05, 2, 6));
    b.wispB.rot(-0.4 * settle(t - 0.08, 2, 6));
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 - 0.06 * k, 1 + 0.08 * k, 1 - 0.06 * k];
  });

/** Dazed: it sags toward the ground and wobbles in a slow circle, hood lolling, claws hanging. */
const wraithStun = (): Clip<RB> =>
  clip<RB>('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6);
    b.root.pos(0, -0.12 + 0.03 * wave(p, 0, 2), 0).rot(0.07 * wave(p, 0.25), 0, 0.07 * wave(p));
    b.body.rot(0.1 + 0.05 * wave(p, 0.1), 0, 0.05 * wave(p, -0.1));
    b.head.rot(0.35 + 0.15 * wave(p, 0.15), 0.3 * cwave(p, 0.15), 0.25 * wave(p, 0.15));
    b.armL.rot(0.55 + 0.1 * wave(p, 0.3), 0, -0.1);
    b.armR.rot(0.55 + 0.1 * wave(p, 0.4), 0, 0.1);
    b.wispA.rot(0.1 * wave(p, -0.2), 0, 0.15 * wave(p, 0.1));
    b.wispB.rot(0.1 * wave(p, -0.4), 0, -0.15 * wave(p, 0.3));
  });

/** It wrenches upward with a soundless shriek, then the hold breaks: the empty robe sinks and crumples on the ground. */
const wraithDie = (): Clip<RB> =>
  clip<RB>('die', 1.15, false, (b, t) => {
    const heap = { root: [0.25, 0, 0, 0, -0.25, 0.05], body: [0.45, 0, 0.1], head: [0.9, 0.2, 0, 0, -0.22, 0.04], armL: [1.2, 0.3, -0.2], armR: [1.1, -0.2, 0.3], wispA: [0.7, 0, 0.4], wispB: [0.6, 0, -0.4] } as const;
    timeline(b, t, [
      [0, {}],
      [0.12, { root: [0, 0, 0, 0, 0.12, -0.04], body: [-0.35, 0, 0], head: [-0.6, 0, 0], armL: [-1.3, 0, -0.55], armR: [-1.25, 0, 0.55], wispA: [-0.3, 0, 0], wispB: [-0.3, 0, 0] }, outExpo],
      [0.45, { root: [0.05, 0, 0, 0, 0.02, 0], body: [0.15, 0, 0.05], head: [0.2, 0, 0], armL: [0.2, 0, -0.2], armR: [0.25, 0, 0.2], wispA: [0.3, 0, 0], wispB: [0.2, 0, 0] }, inOut],
      [0.88, heap, inQuad],
      [1.15, heap, outQuad],
    ], { head: 0.04, armL: 0.03, armR: 0.05, wispA: 0.06, wispB: 0.09 });
  }, (t) => {
    // The robe empties: it settles flat and spreads.
    const k = span(t, 0.5, 0.95, inQuad);
    const bump = Math.max(0, settle(t - 0.9, 3, 10)) * span(t, 0.89, 0.91);
    return [1 + 0.2 * k + 0.05 * bump, 1 - 0.55 * k - 0.05 * bump, 1 + 0.2 * k + 0.05 * bump];
  });

const wraithClips = (): Clip[] => [wraithIdle(), wraithWalk(), wraithAttack(), wraithHit(), wraithStun(), wraithDie()] as Clip[];

/* ---------- gargoyle: stone that flies on heavy wingbeats and dives at towers ---------- */

type AB = 'root' | 'perch' | 'body' | 'head' | 'armL' | 'legL' | 'wingL' | 'armR' | 'legR' | 'wingR';
type AP = KeyPose<AB>;

/** How high it hovers once off its perch. */
const A_LIFT = 0.32;
/** Off its perch (left behind, hidden), legs hanging, claws drawn up. */
const A_AIR: AP = {
  perch: [0, 0, 0, 0, 0, 0, 0.001],
  root: [0, 0, 0, 0, A_LIFT, 0],
  legL: [0.35, 0, -0.08],
  legR: [0.35, 0, 0.08],
  armL: [-0.25, 0, -0.05],
  armR: [-0.25, 0, 0.05],
  head: [-0.1, 0, 0],
};

/** One wingbeat over `p` (0..1): a slow, heavy downstroke and the recovery. `amp` scales it. */
const flap = (b: Bones<AB>, p: number, amp = 1, lean = 0): void => {
  // Downstroke over the first 55 %: the wings spread and drive down; then they fold a little and rise.
  const s = p < 0.55 ? 1 - 2 * smooth(p / 0.55) : -1 + 2 * smooth((p - 0.55) / 0.45);
  const spread = p < 0.55 ? sin((p / 0.55) * Math.PI) : 0;
  const up = 0.55 * s * amp;
  b.wingR.rot(0, -0.45 * spread * amp, up + 0.1);
  b.wingL.rot(0, 0.45 * spread * amp, -up - 0.1);
  // The body lifts on the downstroke and sags on the recovery, a beat late.
  const lift = -cos(TAU * (p - 0.15));
  b.root.pos(0, 0.05 * amp * lift, 0);
  b.body.rot(lean - 0.04 * amp * lift);
  b.head.rot(0.05 * amp * lift);
  b.legL.rot(0.08 * amp * lift);
  b.legR.rot(0.08 * amp * lift);
};

const gargoyleIdle = (): Clip<AB> =>
  clip<AB>('idle', 1.4, true, (b, t) => {
    const p = phase(t, 1.4);
    apply(b, A_AIR);
    flap(b, phase(t, 0.7));
    b.head.rot(0, 0.3 * wave(p, 0.1), 0.05 * wave(p));
    b.root.rot(0, 0.06 * wave(p, 0.3), 0.04 * wave(p));
  });

/** Flying: it leans into the air, legs trailing, wings beating a touch harder. */
const gargoyleWalk = (dur = 1.2): Clip<AB> =>
  clip<AB>('walk', dur, true, (b, t) => {
    const p = phase(t, dur);
    apply(b, A_AIR);
    flap(b, phase(t, dur / 2), 1.15, 0.35);
    b.head.rot(-0.25, 0.08 * wave(p), 0);
    b.legL.rot(0.45);
    b.legR.rot(0.45);
    b.armL.rot(0.1 * wave(p, 0.3));
    b.armR.rot(0.1 * wave(p, 0.8));
    b.root.rot(0, 0, 0.05 * wave(p));
  });

/** Wings flung high, it drops on its prey head first, talons and claws raking, then beats back up. */
const gargoyleAttack = (): Clip<AB> =>
  clip<AB>('attack', 0.72, false, (b, t) => {
    const air: AP = { ...A_AIR, wingL: [0, 0, -0.1], wingR: [0, 0, 0.1] };
    timeline(b, t, [
      [0, air],
      [0.06, { ...air, root: [0, 0, 0, 0, A_LIFT + 0.14, -0.06], body: [-0.35, 0, 0], head: [-0.3, 0, 0], wingL: [0, 0.15, -0.95], wingR: [0, -0.15, 0.95], armL: [-1.2, 0, -0.3], armR: [-1.2, 0, 0.3], legL: [-0.3, 0, -0.1], legR: [-0.3, 0, 0.1] }, outQuad],
      [0.135, { ...air, root: [0, 0, 0, 0, A_LIFT - 0.2, 0.26], body: [0.75, 0, 0], head: [-0.25, 0, 0], wingL: [0, -0.5, 0.35], wingR: [0, 0.5, -0.35], armL: [-0.9, 0, 0.1], armR: [-0.9, 0, -0.1], legL: [-0.75, 0, -0.05], legR: [-0.75, 0, 0.05] }, snap],
      [0.3, { ...air, root: [0, 0, 0, 0, A_LIFT - 0.16, 0.2], body: [0.5, 0, 0], head: [-0.15, 0, 0], wingL: [0, 0.4, -0.45], wingR: [0, -0.4, 0.45], armL: [-0.4, 0, 0.1], armR: [-0.4, 0, -0.1], legL: [-0.2, 0, -0.05], legR: [-0.2, 0, 0.05] }, outBack],
      [0.45, { ...air, root: [0, 0, 0, 0, A_LIFT + 0.04, 0.06], body: [0.15, 0, 0], wingL: [0, -0.3, 0.4], wingR: [0, 0.3, -0.4] }, inOut],
      [0.72, air, inOut],
    ], { head: 0.02, wingL: 0.025, wingR: 0.025, legL: 0.02, legR: 0.03 });
  });

const gargoyleHit = (): Clip<AB> =>
  clip<AB>('hit', 0.4, false, (b, t) => {
    apply(b, A_AIR);
    const k = settle(t, 2.2, 8) * span(t, 0, 0.03, outQuad);
    const a = Math.abs(k);
    b.root.pos(0, -0.07 * a, -0.09 * k);
    b.body.rot(-0.3 * k, 0.1 * k, 0.08 * k);
    b.head.rot(-0.35 * k, -0.2 * k, 0);
    b.wingL.rot(0, 0, -0.5 * settle(t - 0.03, 2.4, 7));
    b.wingR.rot(0, 0, 0.5 * settle(t - 0.03, 2.4, 7));
    b.armL.rot(0.3 * k, 0, -0.2 * k);
    b.armR.rot(0.3 * k, 0, 0.2 * k);
  });

/** Dazed: lift failing, it lurches low over the ground on ragged, uneven beats. */
const gargoyleStun = (): Clip<AB> =>
  clip<AB>('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6);
    apply(b, A_AIR);
    flap(b, phase(t, 0.8), 0.6, 0.15);
    b.root.pos(0, -0.18 + 0.04 * wave(p, 0.2), 0).rot(0.05 * wave(p, 0.25), 0, 0.14 * wave(p));
    b.wingR.rot(0, 0, 0.15 * wave(p, 0.1));
    b.wingL.rot(0, 0, 0.2 * wave(p, 0.4));
    b.head.rot(0.3 + 0.12 * wave(p, 0.15, 2), 0.3 * cwave(p, 0.15), 0.2 * wave(p, 0.15));
    b.armL.rot(0.4, 0, -0.1);
    b.armR.rot(0.4, 0, 0.1);
  });

/** The magic leaves the stone: wings stiffen, it drops like a rock, hits the ground and topples onto its side. */
const gargoyleDie = (): Clip<AB> =>
  clip<AB>('die', 1.05, false, (b, t) => {
    const down: AP = { perch: A_AIR.perch!, root: [0, 0, 1.3, 0.25, -0.02, 0], body: [0.2, 0, 0], head: [0.3, 0.3, 0], wingL: [0, 0.3, -0.25], wingR: [0, -0.2, 0.55], armL: [-0.6, 0, -0.2], armR: [-0.3, 0, 0.3], legL: [-0.5, 0, -0.1], legR: [-0.2, 0, 0.1] };
    timeline(b, t, [
      [0, A_AIR],
      [0.1, { ...A_AIR, root: [0, 0, 0, 0, A_LIFT + 0.08, 0], body: [-0.3, 0, 0], head: [-0.45, 0, 0], wingL: [0, 0, -0.9], wingR: [0, 0, 0.9], armL: [-0.5, 0, -0.4], armR: [-0.5, 0, 0.4] }, outExpo],
      [0.45, { ...A_AIR, root: [0, 0, 0, 0, -0.32, 0.03], body: [0.3, 0, 0], head: [0.2, 0, 0], wingL: [0, 0.2, -0.6], wingR: [0, -0.2, 0.6], legL: [-0.2, 0, -0.1], legR: [-0.2, 0, 0.1] }, inQuad],
      [0.75, down, inQuad],
      [1.05, down, outQuad],
    ], { head: 0.03, wingL: 0.04, wingR: 0.05 });
  }, (t) => {
    const k = Math.max(0, settle(t - 0.45, 3, 10)) * span(t, 0.44, 0.46);
    return [1 + 0.1 * k, 1 - 0.12 * k, 1 + 0.1 * k];
  });

/** Wakes on its perch: a shiver through the stone, the head lifts, then it tears free and the perch crumbles away. */
const gargoyleSpawn = (): Clip<AB> =>
  clip<AB>('spawn', 1.1, false, (b, t) => {
    const shiver = tremble(t - 0.1, 14, 3) * envelope(t, 0.1, 0.15, 0.4, 0.5);
    b.body.rot(0, 0, 0.03 * shiver);
    timeline(b, t, [
      [0, {}],
      [0.35, { head: [-0.25, 0.2, 0], wingL: [0, 0.2, -0.2], wingR: [0, -0.2, 0.2] }, inOut],
      [0.5, { body: [0.15, 0, 0], head: [-0.1, 0, 0], wingL: [0, 0.3, 0.3], wingR: [0, -0.3, -0.3], legL: [-0.2, 0, 0], legR: [-0.2, 0, 0], root: [0, 0, 0, 0, -0.03, 0] }, outQuad],
      [0.75, { ...A_AIR, perch: [0, 0, 0, 0, -A_LIFT - 0.1, 0, 0.5], root: [0, 0, 0, 0, A_LIFT + 0.06, 0], wingL: [0, -0.3, -0.85], wingR: [0, 0.3, 0.85] }, outCubic],
      [0.85, { ...A_AIR, root: [0, 0, 0, 0, A_LIFT + 0.08, 0], wingL: [0, 0.4, 0.4], wingR: [0, -0.4, -0.4] }, inOut],
      [1.1, { ...A_AIR, wingL: [0, 0, -0.1], wingR: [0, 0, 0.1] }, inOut],
    ], { head: 0.03, wingL: 0.03, wingR: 0.03 });
  });

const gargoyleClips = (): Clip[] => [gargoyleIdle(), gargoyleWalk(), gargoyleAttack(), gargoyleHit(), gargoyleStun(), gargoyleDie(), gargoyleSpawn()] as Clip[];

/* ---------- slime: a heaving mass of ink with things drowned in it ---------- */

type LB = 'root' | 'body' | 'core' | 'bone0' | 'bone1' | 'bone2' | 'blade';

/** What it has swallowed sloshes inside, `lag` cycles behind a motion `m(phase)`. */
const slosh = (b: Bones<LB>, f: (lag: number) => [number, number, number]): void => {
  const parts: [LB, number][] = [['core', 0.06], ['bone0', 0.1], ['bone1', 0.14], ['bone2', 0.18], ['blade', 0.12]];
  for (const [n, lag] of parts) {
    const [x, y, z] = f(lag);
    b[n].pos(x, y, z);
  }
};

const slimeIdle = (): Clip<LB> =>
  clip<LB>('idle', 2.4, true, (b, t) => {
    const p = phase(t, 2.4);
    const h = wave(p, 0, 2);
    b.body.rot(0.04 * wave(p, 0.1), 0, 0.05 * wave(p, 0.35)).scale(1 + 0.015 * h);
    slosh(b, (lag) => [0.012 * wave(p, 0.35 - lag), 0.02 * wave(p, -lag, 2), 0.01 * wave(p, 0.1 - lag)]);
    b.core.rot(0.15 * wave(p, -0.1), 0, 0.1 * wave(p, 0.25));
    b.blade.rot(0, 0, 0.08 * wave(p, 0.2));
  }, (t) => vol(0.06 * wave(phase(t, 2.4), 0, 2)));

/** It heaves itself along: gathers up tall, then surges and spreads forward, over and over. */
const slimeWalk = (dur = 0.9): Clip<LB> =>
  clip<LB>('walk', dur, true, (b, t) => {
    const p = phase(t, dur);
    b.body.rot(0.13 * wave(p, -0.05), 0, 0.03 * wave(p, 0.25)).pos(0, 0, 0.03 * wave(p, -0.2));
    slosh(b, (lag) => [0, 0.025 * wave(p, 0.25 - lag), -0.035 * wave(p, -lag)]);
    b.core.rot(-0.2 * wave(p, -0.15), 0, 0);
    b.bone0.rot(0.15 * wave(p, -0.2));
    b.blade.rot(0, 0, 0.1 * wave(p, -0.1));
  }, (t) => {
    const p = phase(t, dur);
    const k = 0.13 * wave(p, 0.25);
    const [sx, sy] = vol(k);
    return [sx, sy, sx * (1 + 0.06 * wave(p, 0))];
  });

/** Squats and swells, then throws itself forward and spreads over its prey to engulf it, and draws back. */
const slimeAttack = (): Clip<LB> =>
  clip<LB>('attack', 0.64, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.07, { root: [0, 0, 0, 0, 0, -0.05], body: [-0.2, 0, 0, 0, 0, 0, 1.04] }, outQuad],
      [0.135, { root: [0, 0, 0, 0, 0.02, 0.28], body: [0.5, 0, 0, 0, 0, 0, 1.14] }, snap],
      [0.32, { root: [0, 0, 0, 0, 0, 0.24], body: [0.38, 0, 0, 0, 0, 0, 1.1] }, outQuad],
      [0.64, {}, inOut],
    ]);
    const w = settle(t - 0.135, 3.5, 7) * span(t, 0.13, 0.15);
    b.body.rot(0.06 * w, 0, 0.05 * w);
    slosh(b, (lag) => [0, 0.03 * settle(t - 0.07 - lag * 0.5, 3, 7) * span(t, 0.06, 0.1), -0.05 * envelope(t - lag * 0.5, 0.0, 0.07, 0.07, 0.14) + 0.06 * envelope(t - lag * 0.5, 0.1, 0.18, 0.3, 0.6)]);
    b.core.rot(0.4 * envelope(t, 0.1, 0.2, 0.3, 0.6), 0, 0);
  }, (t) => {
    const sx = keys(t, [[0, 1], [0.07, 1.16, outQuad], [0.11, 0.9, inQuad], [0.14, 1.26, outQuad], [0.3, 1.16], [0.64, 1, inOut]]);
    const sy = keys(t, [[0, 1], [0.07, 0.76, outQuad], [0.11, 1.12, inQuad], [0.14, 0.72, outQuad], [0.3, 0.82], [0.64, 1, inOut]]);
    const sz = keys(t, [[0, 1], [0.07, 1.12, outQuad], [0.11, 1.3, inQuad], [0.14, 1.32, outQuad], [0.3, 1.18], [0.64, 1, inOut]]);
    const w = 0.05 * settle(t - 0.14, 4, 6) * span(t, 0.13, 0.15);
    return [sx + w, sy - w, sz + w];
  });

const slimeHit = (): Clip<LB> =>
  clip<LB>('hit', 0.42, false, (b, t) => {
    const k = settle(t, 2.6, 7) * span(t, 0, 0.03, outQuad);
    b.body.rot(-0.22 * k, 0, 0.08 * k).pos(0, 0, -0.06 * k);
    slosh(b, (lag) => [0, 0.04 * settle(t - lag, 3, 7), -0.05 * settle(t - lag, 2.6, 7)]);
    b.core.rot(-0.4 * settle(t - 0.04, 2.5, 6), 0, 0);
  }, (t) => {
    const k = settle(t, 4, 7) * span(t, 0, 0.03, outQuad);
    return vol(-0.22 * k);
  });

/** Dazed: it sags, wobbling loose and slow, and what's inside drifts in circles. */
const slimeStun = (): Clip<LB> =>
  clip<LB>('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6);
    b.body.rot(0.08 * wave(p, 0.25), 0, 0.09 * wave(p));
    slosh(b, (lag) => [0.04 * wave(p, -lag * 2), -0.03 + 0.015 * wave(p, -lag, 2), 0.04 * cwave(p, -lag * 2)]);
    b.core.rot(0.2 * wave(p, 0.1), 0.6 * wave(p), 0.25 * wave(p, 0.3));
  }, (t) => {
    const p = phase(t, 1.6);
    const [sx, sy, sz] = vol(-0.14 + 0.05 * wave(p, 0, 2));
    return [sx * (1 + 0.04 * wave(p)), sy, sz * (1 - 0.04 * wave(p))];
  });

/** It quivers, swells, and gives way: it spills flat into a puddle and what it held falls out. */
const slimeDie = (): Clip<LB> =>
  clip<LB>('die', 1.05, false, (b, t) => {
    const q = tremble(t, 18, 2) * (1 - span(t, 0.3, 0.45));
    b.body.rot(0.05 * q, 0, 0.06 * q);
    timeline(b, t, [
      [0, {}],
      [0.32, { body: [-0.05, 0, 0, 0, 0, 0, 1.12] }, outQuad],
      [0.7, { body: [0.06, 0, 0.04, 0, 0, 0, 1.08], core: [0.9, 0.4, 0.3, 0.02, -0.25, 0.08], bone0: [1.2, 0, 0.5, 0, -0.22, 0.05], bone1: [-0.8, 0, 0.6, 0.05, -0.24, 0], bone2: [0.6, 0.6, -0.9, -0.03, -0.25, 0.04], blade: [0, 0, 0.75, 0.08, -0.33, 0.05] }, inQuad],
      [1.05, { body: [0.06, 0, 0.04, 0, 0, 0, 1.08], core: [1.0, 0.5, 0.3, 0.02, -0.27, 0.09], bone0: [1.3, 0, 0.5, 0, -0.23, 0.05], bone1: [-0.85, 0, 0.6, 0.05, -0.25, 0], bone2: [0.6, 0.6, -0.9, -0.03, -0.26, 0.04], blade: [0, 0, 0.75, 0.08, -0.34, 0.05] }, outBack],
    ], { core: 0.03, bone0: 0.05, bone1: 0.02, bone2: 0.06, blade: 0.04 });
  }, (t) => {
    const swell = span(t, 0.05, 0.32, outQuad) * (1 - span(t, 0.32, 0.45));
    const spill = span(t, 0.35, 0.72, inQuad);
    const w = 0.08 * settle(t - 0.72, 3, 6) * span(t, 0.71, 0.73);
    const sy = 1 + 0.08 * swell - 0.72 * spill - w;
    const sxz = 1 + 0.04 * swell + 0.6 * spill + w;
    return [sxz, sy, sxz];
  });

const slimeClips = (): Clip[] => [slimeIdle(), slimeWalk(), slimeAttack(), slimeHit(), slimeStun(), slimeDie()] as Clip[];

/* ---------- stalker: a low, patient hunter that slinks and pounces ---------- */

type KB = 'root' | 'hips' | 'chest' | 'head' | 'jaw' | 'armL' | 'foreL' | 'legL' | 'shinL' | 'footL' | 'armR' | 'foreR' | 'legR' | 'shinR' | 'footR';
type KP = KeyPose<KB>;

/** Belly low, head level and forward, claws resting on the ground ahead. */
const K_STANCE: KP = {
  root: [0, 0, 0, 0, -0.03, 0],
  hips: [0.04, 0, 0],
  chest: [0.06, 0, 0],
  head: [-0.1, 0, 0],
  legL: [-0.12, 0, 0],
  legR: [-0.12, 0, 0],
  shinL: [0.06, 0, 0],
  shinR: [0.06, 0, 0],
  footL: [0.06, 0, 0],
  footR: [0.06, 0, 0],
  armL: [-0.05, 0, 0],
  armR: [-0.05, 0, 0],
  foreL: [0.02, 0, 0],
  foreR: [0.02, 0, 0],
};

const stalkerIdle = (): Clip<KB> =>
  clip<KB>('idle', 4.2, true, (b, t) => {
    const p = phase(t, 4.2);
    apply(b, K_STANCE);
    const br = wave(p, 0, 3);
    b.chest.rot(-0.02 * br).scale(1 + 0.012 * br);
    b.root.pos(0, -0.008 * (1 + br), 0);
    // Utterly still, but for the head: it turns slow and holds, as if it had found you.
    const look = smooth(clamp01(wave(p, 0.05) * 1.8)) - smooth(clamp01(-wave(p, 0.05) * 1.8));
    b.head.rot(0.03 * br + 0.04 * look * look, 0.42 * look, 0.08 * look);
    b.chest.rot(0, 0.06 * look, 0);
    b.jaw.rot(0.08 * pos0(wave(p, 0.6)) ** 6);
    // Claws flex one finger at a time against the ground.
    b.foreL.rot(-0.08 * pos0(wave(p, 0.2, 3)) ** 4);
    b.foreR.rot(-0.08 * pos0(wave(p, 0.7, 3)) ** 4);
    b.hips.rot(0, 0.03 * wave(p, 0.3), 0.02 * wave(p));
  });

/**
 * A slinking prowl on all fours, a long arm for each foreleg: one limb at a time (hind, fore, hind, fore),
 * shoulders rolling, belly low, the head held level and dead ahead whatever the body does.
 */
const stalkerWalk = (dur = 1.1): Clip<KB> =>
  clip<KB>('walk', dur, true, (b, t) => {
    const p = phase(t, dur);
    apply(b, K_STANCE);
    const leg = (side: 'L' | 'R', off: number): void => {
      const s = wave(p, -off), c = cwave(p, -off);
      b[`leg${side}`].rot(-0.42 * s);
      b[`shin${side}`].rot(-0.35 * pos0(c) ** 1.3);
      b[`foot${side}`].rot(0.45 * pos0(c) ** 1.3 + 0.1 * s);
    };
    const arm = (side: 'L' | 'R', off: number): void => {
      const s = wave(p, -off), c = cwave(p, -off);
      b[`arm${side}`].rot(-0.38 * s - 0.08 * pos0(c));
      b[`fore${side}`].rot(-0.45 * pos0(c) ** 1.4);
    };
    leg('L', 0);
    arm('L', 0.25);
    leg('R', 0.5);
    arm('R', 0.75);
    b.root.pos(0, 0.015 * wave(p, 0.1, 2) - 0.03, 0);
    b.hips.rot(0, 0.08 * wave(p, 0.1), 0.05 * wave(p, 0.1));
    b.chest.rot(0.02 * wave(p, 0.3, 2), -0.12 * wave(p, 0.35), -0.06 * wave(p, 0.35));
    // The head steadies against the shoulders.
    b.head.rot(-0.02 * wave(p, 0.3, 2), 0.13 * wave(p, 0.35), 0.06 * wave(p, 0.35));
    b.jaw.rot(0.05);
  }, (t) => {
    const k = wave(phase(t, dur), 0.35, 2);
    return [1 + 0.01 * k, 1 - 0.015 * k, 1 + 0.01 * k];
  });

/** Sinks lower, coils, and springs: arms flung up and out, it lands on the prey claws first, jaw open, and sinks back. */
const stalkerAttack = (): Clip<KB> =>
  clip<KB>('attack', 0.7, false, (b, t) => {
    timeline(b, t, [
      [0, K_STANCE],
      [0.08, { ...K_STANCE, root: [0, 0, 0, 0, -0.1, -0.06], hips: [0.1, 0, 0], chest: [0.22, 0, 0], head: [-0.35, 0, 0], jaw: [0.2, 0, 0], armL: [0.35, 0, -0.1], armR: [0.35, 0, 0.1], foreL: [-0.2, 0, 0], foreR: [-0.2, 0, 0], legL: [-0.45, 0, 0], legR: [-0.45, 0, 0], shinL: [-0.35, 0, 0], shinR: [-0.35, 0, 0], footL: [0.5, 0, 0], footR: [0.5, 0, 0] }, outQuad],
      [0.115, { ...K_STANCE, root: [0, 0, 0, 0, 0.1, 0.18], hips: [-0.15, 0, 0], chest: [-0.45, 0, 0], head: [0.25, 0, 0], jaw: [0.65, 0, 0], armL: [-1.9, 0, -0.35], armR: [-1.9, 0, 0.35], foreL: [-0.5, 0, 0], foreR: [-0.5, 0, 0], legL: [0.45, 0, 0], legR: [0.4, 0, 0], shinL: [0.2, 0, 0], shinR: [0.2, 0, 0], footL: [-0.3, 0, 0], footR: [-0.3, 0, 0] }, inQuad],
      [0.15, { ...K_STANCE, root: [0, 0, 0, 0, 0.02, 0.32], hips: [0.0, 0, 0], chest: [0.15, 0, 0], head: [0.05, 0, 0], jaw: [0.55, 0, 0], armL: [-0.75, 0, -0.1], armR: [-0.75, 0, 0.1], foreL: [0.1, 0, 0], foreR: [0.1, 0, 0], legL: [0.3, 0, 0], legR: [0.25, 0, 0], shinL: [0.1, 0, 0], shinR: [0.1, 0, 0], footL: [-0.15, 0, 0], footR: [-0.15, 0, 0] }, snap],
      [0.34, { ...K_STANCE, root: [0, 0, 0, 0, -0.07, 0.3], hips: [0.08, 0, 0], chest: [0.3, 0, 0], head: [-0.15, 0, 0], jaw: [0.2, 0, 0], armL: [-0.3, 0, -0.05], armR: [-0.3, 0, 0.05], foreL: [0.15, 0, 0], foreR: [0.15, 0, 0], legL: [-0.35, 0, 0], legR: [-0.3, 0, 0], shinL: [-0.2, 0, 0], shinR: [-0.2, 0, 0], footL: [0.35, 0, 0], footR: [0.35, 0, 0] }, outQuad],
      [0.7, K_STANCE, inOut],
    ], { head: 0.012, jaw: 0.01, foreL: 0.012, foreR: 0.02, footL: 0.01, footR: 0.015 });
  }, (t) => {
    const s = keys(t, [[0, 0], [0.08, -0.08, outQuad], [0.115, 0.1, inQuad], [0.16, -0.05], [0.34, -0.03], [0.7, 0, inOut]]);
    return vol(s);
  });

const stalkerHit = (): Clip<KB> =>
  clip<KB>('hit', 0.38, false, (b, t) => {
    apply(b, K_STANCE);
    const k = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    const a = Math.abs(k);
    b.root.pos(0, 0.02 * a, -0.09 * k);
    b.chest.rot(-0.32 * k, 0.15 * k, 0.05 * k);
    b.head.rot(-0.35 * settle(t - 0.02, 2.4, 8) * span(t, 0, 0.05), -0.25 * k, 0);
    b.jaw.rot(0.55 * a);
    b.armL.rot(-0.45 * k, 0, -0.2 * k);
    b.armR.rot(-0.4 * k, 0, 0.2 * k);
    b.foreL.rot(-0.3 * a);
    b.foreR.rot(-0.3 * a);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.07 * k, 1 - 0.1 * k, 1 + 0.07 * k];
  });

/** Dazed: it sways on its haunches, shaking its head slowly, claws hanging. */
const stalkerStun = (): Clip<KB> =>
  clip<KB>('stun', 1.5, true, (b, t) => {
    const p = phase(t, 1.5);
    apply(b, K_STANCE, 0.6);
    b.root.pos(0, -0.04, 0).rot(0.03 * wave(p, 0.25), 0, 0.08 * wave(p));
    b.chest.rot(0.12 + 0.05 * wave(p, 0, 2), 0.05 * wave(p, 0.1), -0.06 * wave(p));
    b.head.rot(0.3 + 0.08 * wave(p, 0.2, 2), 0.4 * wave(p, 0.15), 0.2 * wave(p, 0.2));
    b.jaw.rot(0.3 + 0.06 * wave(p, 0, 2));
    b.armL.rot(0.12 * wave(p, 0.3), 0, -0.05);
    b.armR.rot(-0.12 * wave(p, 0.3), 0, 0.05);
    b.foreL.rot(0.1);
    b.foreR.rot(0.1);
  });

/** It rears up writhing, then falls forward on its belly, limbs splayed. */
const stalkerDie = (): Clip<KB> =>
  clip<KB>('die', 1.05, false, (b, t) => {
    const down: KP = { root: [0.12, 0.2, 0.2, 0, -0.36, 0.08], hips: [0.1, 0, 0], chest: [0.12, 0, 0.05], head: [0.45, 0.45, 0.25], jaw: [0.35, 0, 0], armL: [-1.25, 0.3, -0.45], foreL: [0.25, 0, 0], armR: [-0.9, -0.2, 0.55], foreR: [0.35, 0, 0], legL: [0.55, 0, -0.45], shinL: [0.5, 0, 0], footL: [-0.3, 0, 0], legR: [0.85, 0, 0.35], shinR: [0.3, 0, 0], footR: [-0.2, 0, 0] };
    timeline(b, t, [
      [0, K_STANCE],
      [0.14, { ...K_STANCE, root: [0, 0, 0, 0, 0.05, -0.05], hips: [-0.1, 0, 0], chest: [-0.6, 0.15, 0.1], head: [-0.6, -0.2, 0], jaw: [0.75, 0, 0], armL: [-1.8, 0, -0.6], armR: [-1.5, 0, 0.7], foreL: [-0.6, 0, 0], foreR: [-0.4, 0, 0] }, outExpo],
      [0.45, { ...K_STANCE, root: [0, 0, 0.1, 0, -0.1, 0], chest: [-0.2, 0.25, 0.15], head: [0.2, 0.3, 0.1], jaw: [0.5, 0, 0], armL: [-0.8, 0, -0.4], armR: [-0.4, 0, 0.5], legL: [-0.5, 0, -0.1], shinL: [-0.4, 0, 0], legR: [-0.45, 0, 0.1], shinR: [-0.4, 0, 0] }, inOut],
      [0.75, down, inQuad],
      [1.05, down, outQuad],
    ], { head: 0.04, jaw: 0.02, foreL: 0.03, foreR: 0.04 });
  }, (t) => {
    const k = Math.max(0, settle(t - 0.75, 3, 10)) * span(t, 0.74, 0.76);
    return [1 + 0.07 * k, 1 - 0.09 * k, 1 + 0.07 * k];
  });

const stalkerClips = (): Clip[] => [stalkerIdle(), stalkerWalk(), stalkerAttack(), stalkerHit(), stalkerStun(), stalkerDie()] as Clip[];

/* ---------- bone wall: a barrier of ribs that creaks, shudders and falls ---------- */

type XB = 'root' | 'body' | 'ribsL' | 'ribsC' | 'ribsR' | 'bind' | 'skull0' | 'skull1' | 'skull2';

/** The ribs flex out and in like a slow breath, one clump after the other; the skulls stir. */
const wallBreath = (b: Bones<XB>, p: number, amp: number): void => {
  b.ribsL.rot(0.02 * amp * wave(p, 0.1), 0, 0.035 * amp * wave(p));
  b.ribsC.rot(-0.025 * amp * wave(p, -0.05), 0, 0).scale(1 + 0.012 * amp * wave(p, -0.1));
  b.ribsR.rot(0.02 * amp * wave(p, -0.1), 0, -0.035 * amp * wave(p, -0.2));
  b.bind.pos(0, 0.006 * amp * wave(p, -0.15), 0).rot(0, 0, 0.01 * amp * wave(p, 0.2));
  b.body.scale(1 + 0.006 * amp * wave(p, -0.05));
};

const wallIdle = (): Clip<XB> =>
  clip<XB>('idle', 4, true, (b, t) => {
    const p = phase(t, 4);
    wallBreath(b, p, 1);
    b.skull0.rot(0.12 * pos0(wave(p, 0.3)) ** 10, 0.1 * wave(p, 0.2), 0);
    b.skull1.rot(0, -0.12 * wave(p, 0.6), 0);
    b.skull2.rot(-0.08 * pos0(wave(p, 0.8, 2)) ** 10, 0, 0);
  });

/** It can't walk; it groans, the creak running along it. */
const wallWalk = (): Clip<XB> =>
  clip<XB>('walk', 2, true, (b, t) => {
    const p = phase(t, 2);
    wallBreath(b, p, 1.6);
    b.body.rot(0.02 * wave(p, 0.2), 0, 0.012 * wave(p));
  });

/** A rattling surge: the ribs lash forward at whatever is beside it and spring back. */
const wallAttack = (): Clip<XB> =>
  clip<XB>('attack', 0.6, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.07, { body: [-0.06, 0, 0], ribsL: [-0.12, 0, 0], ribsC: [-0.15, 0, 0], ribsR: [-0.12, 0, 0] }, outQuad],
      [0.135, { body: [0.08, 0, 0], ribsL: [0.3, 0, 0.04], ribsC: [0.36, 0, 0], ribsR: [0.3, 0, -0.04], bind: [0.1, 0, 0], skull0: [0, 0, 0, 0, 0.04, 0], skull1: [0, 0, 0, 0, 0.03, 0], skull2: [0, 0, 0, 0, 0.05, 0] }, snap],
      [0.6, {}, outQuad],
    ], { ribsL: 0.02, ribsR: 0.03, bind: 0.02, skull0: 0.01, skull1: 0.03, skull2: 0.02 });
    const r = tremble(t - 0.14, 12, 7);
    b.ribsC.rot(0.06 * r);
    b.ribsL.rot(0, 0, 0.04 * r);
    b.ribsR.rot(0, 0, -0.04 * r);
  });

const wallHit = (): Clip<XB> =>
  clip<XB>('hit', 0.42, false, (b, t) => {
    const r = tremble(t, 13, 8);
    const k = settle(t, 2.4, 9) * span(t, 0, 0.03);
    b.body.rot(-0.07 * k, 0, 0.03 * r);
    b.ribsL.rot(-0.08 * settle(t - 0.02, 2.4, 9), 0, 0.06 * tremble(t - 0.01, 13, 8));
    b.ribsC.rot(-0.1 * k + 0.04 * r, 0, 0);
    b.ribsR.rot(-0.08 * settle(t - 0.03, 2.4, 9), 0, -0.06 * tremble(t - 0.02, 13, 8));
    b.bind.pos(0, 0.02 * Math.abs(r), 0);
    b.skull0.pos(0, 0.03 * pos0(sin(Math.min(1, t / 0.2) * Math.PI)), 0);
    b.skull1.pos(0, 0.025 * pos0(sin(Math.min(1, (t - 0.03) / 0.18) * Math.PI)), 0);
    b.skull2.pos(0, 0.035 * pos0(sin(Math.min(1, (t - 0.02) / 0.22) * Math.PI)), 0);
  });

const wallStun = (): Clip<XB> =>
  clip<XB>('stun', 1.2, true, (b, t) => {
    const p = phase(t, 1.2);
    wallBreath(b, p, 2.2);
    b.body.rot(0.025 * wave(p, 0.25), 0, 0.02 * wave(p));
  });

/** The magic gives out: the clumps of ribs fall apart outward, the binding bones drop, the skulls roll away. */
const wallDie = (): Clip<XB> =>
  clip<XB>('die', 1.1, false, (b, t) => {
    const down = { body: [0, 0, 0, 0, -0.04, 0], ribsL: [1.25, 0.2, 0.55, 0, -0.03, 0.02], ribsC: [-1.35, 0, 0.1, 0, -0.03, 0], ribsR: [1.2, -0.2, -0.3, 0, -0.03, 0.02], bind: [0.25, 0, 0.05, 0, -0.27, 0.12], skull0: [0.6, 0, 1.2, -0.06, -0.03, 0.1], skull1: [-0.8, 0.5, 0, 0.04, -0.03, -0.08], skull2: [0.9, 0, -1.4, 0.08, -0.03, 0.06] } as const;
    timeline(b, t, [
      [0, {}],
      [0.12, { body: [0, 0, 0, 0, 0.02, 0], ribsL: [-0.05, 0, -0.08], ribsC: [0.06, 0, 0], ribsR: [-0.05, 0, 0.08], bind: [0, 0, 0, 0, 0.03, 0] }, outExpo],
      [0.7, down, inQuad],
      [0.84, { ...down, ribsL: [1.12, 0.2, 0.5, 0, -0.03, 0.02], ribsC: [-1.22, 0, 0.1, 0, -0.03, 0], ribsR: [1.08, -0.2, -0.28, 0, -0.03, 0.02] }, outQuad],
      [1.1, down, inQuad],
    ], { ribsC: 0.04, ribsR: 0.08, bind: 0.12, skull0: 0.15, skull1: 0.2, skull2: 0.18 });
  });

/** It claws up out of the ground: the ribs rise splayed and close into a wall, the skulls surface last. */
const wallSpawn = (): Clip<XB> =>
  clip<XB>('spawn', 1.1, false, (b, t) => {
    timeline(b, t, [
      [0, { body: [0, 0, 0, 0, -0.9, 0, 0.7], ribsL: [0.5, 0, 0.4], ribsC: [-0.5, 0, 0], ribsR: [0.5, 0, -0.4], bind: [0, 0, 0, 0, -0.5, 0], skull0: [0, 0, 0, 0, -0.25, 0], skull1: [0, 0, 0, 0, -0.25, 0], skull2: [0, 0, 0, 0, -0.25, 0] }],
      [0.6, { body: [0, 0, 0, 0, 0.03, 0, 1.02], ribsL: [-0.08, 0, -0.05], ribsC: [0.08, 0, 0], ribsR: [-0.08, 0, 0.05] }, outCubic],
      [1.1, {}, outBack],
    ], { ribsL: 0.06, ribsC: 0.03, ribsR: 0.09, bind: 0.15, skull0: 0.3, skull1: 0.4, skull2: 0.35 });
  });

const wallClips = (): Clip[] => [wallIdle(), wallWalk(), wallAttack(), wallHit(), wallStun(), wallDie(), wallSpawn()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = {
  goblin: goblinClips,
  wolf: wolfClips,
  skeleton: skeletonClips,
  wraith: wraithClips,
  gargoyle: gargoyleClips,
  slime: slimeClips,
  stalker: stalkerClips,
  boneWall: wallClips,
};
